// ============================================================================
// NOTICE TYPE MASTER SCREEN — MSTR-NOTTYPE-001
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useNoticeTypes } from 'hooks/api/useNoticeTypes';
import { noticeTypeService } from 'api/services/noticeTypeService';
import type { NoticeType, NoticeTypeSearchRequest } from 'api/types/noticeType.types';
import NoticeTypeFormModal from 'components/modals/modules/notice-type-module/NoticeTypeFormModal';

type ViewMode = 'list' | 'grid';

interface NoticeTypeMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const NoticeTypeMasterScreen: React.FC<NoticeTypeMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<NoticeTypeSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, loading, pagination, refetch } = useNoticeTypes(queryParams);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [noticeTypeCodeFilter, setNoticeTypeCodeFilter] = useState('');
  const [noticeTypeNameFilter, setNoticeTypeNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery.trim() || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selectedNT, setSelectedNT] = useState<NoticeType | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(n => n.noticeTypeCode === recordCode);
        if (rec) setSelectedNT(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [ntToDelete, setNTToDelete] = useState<NoticeType | null>(null);

  const handleAddNew = useCallback(() => {
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedNT(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback(
    (nt: NoticeType) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${nt.noticeTypeCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedNT(nt);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleView = useCallback(
    (nt: NoticeType) => {
      const modalId = tabId ? `${tabId}-view-modal-${nt.noticeTypeCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedNT(nt);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleCopy = useCallback(
    (nt: NoticeType) => {
      setSelectedNT(nt);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [],
  );

  const handleDeleteClick = useCallback((nt: NoticeType) => {
    setNTToDelete(nt);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!ntToDelete) return;
    setIsDeleting(true);
    try {
      await noticeTypeService.delete(ntToDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Notice Type "${ntToDelete.noticeTypeCode}" deleted successfully.`);
      setNTToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const msg = err?.response?.data?.message || 'An error occurred while deleting.';
      notificationService.toast.error(msg, 6000);
    }
  }, [ntToDelete, refetch]);

  const handleSaved = useCallback(() => { refetch(); }, [refetch]);
  const handleRefresh = useCallback(() => { refetch(); }, [refetch]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      noticeTypeCode: noticeTypeCodeFilter.trim() || undefined,
      noticeTypeName: noticeTypeNameFilter.trim() || undefined,
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [noticeTypeCodeFilter, noticeTypeNameFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setNoticeTypeCodeFilter('');
    setNoticeTypeNameFilter('');
    setStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.noticeTypeCode) count++;
    if (queryParams.noticeTypeName) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.noticeTypeCode, queryParams.noticeTypeName, queryParams.statusCode]);

  const columns: SyutGridColumn<NoticeType>[] = useMemo(() => [
    {
      id: 'noticeTypeCode',
      header: 'Notice Type Code',
      accessorKey: 'noticeTypeCode',
      size: 180,
      enableSorting: true,
    },
    {
      id: 'noticeTypeName',
      header: 'Notice Type Name',
      accessorKey: 'noticeTypeName',
      size: 320,
      enableSorting: true,
      flex: true,
    },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => {
        const n = row.original;
        const code = n.deletedAt ? 'DELETED' : (n.statusCode || 'ACTIVE');
        let color = '#6b7280';
        if (code === 'ACTIVE') color = '#16a34a';
        else if (code === 'DELETED') color = '#dc2626';
        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color,
              padding: '2px 8px',
              border: `1px solid ${color}`,
              borderRadius: '999px',
            }}
            title={n.deletedAt ? `Deleted at ${new Date(n.deletedAt).toLocaleString()}` : undefined}
          >
            {code}
          </span>
        );
      },
    },
    {
      id: 'updatedAt',
      header: 'Last Updated',
      accessorKey: 'updatedAt',
      size: 150,
      enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.updatedAt;
        if (!v) return '-';
        try {
          return new Date(v).toLocaleString();
        } catch {
          return v;
        }
      },
    },
  ], []);

  // Column visibility (driven by ColumnSelector toolbar control)
  const columnDefinitions: ColumnDefinition[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id as string,
        label: typeof col.header === 'string' ? (col.header as string) : (col.id as string),
        defaultVisible: true,
      })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
  }, [columnDefinitions]);

  const getRowActions = useCallback((nt: NoticeType): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />, onClick: () => handleView(nt) },
    { id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={16} />,
      onClick: () => handleEdit(nt),
    },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={16} />, onClick: () => handleCopy(nt) },
    { id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={16} />,
      onClick: () => handleDeleteClick(nt),
      variant: 'danger',
    },
  ], [handleEdit, handleView, handleCopy, handleDeleteClick]);

  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add',
      tooltip: 'Add New Notice Type',
      onClick: handleAddNew,
      variant: 'icon',
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Refresh',
      onClick: handleRefresh,
      variant: 'icon',
    },
  ], [handleAddNew, handleRefresh]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Notice Type Master"
        description="Classification of legal notices (Arbitration Notice, Due Notice, Section 138 Demand, SARFAESI, etc.). Referenced by Notice Template."
        icon={<IconSystem.mail size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
                visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
                rowActions={getRowActions}
                isLoading={loading}
                emptyMessage="No notice types found"
              />
            )}

            {viewMode === 'grid' && (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: '1rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.map((n) => (
                    <div
                      key={String(n.id)}
                      style={{
                        position: 'relative',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleEdit(n)}
                    >
                      {/* Action menu pinned to the card's top-right; stop click
                          propagation so opening the menu doesn't also fire the
                          card's onClick. */}
                      <div
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionMenu items={getRowActions(n)} />
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600, paddingRight: '1.5rem' }}>
                        {n.noticeTypeCode}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem', color: 'var(--text-primary)' }}>
                        {n.noticeTypeName}
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: n.statusCode === 'ACTIVE' ? '#16a34a' : '#6b7280' }}>
                        {n.statusCode || 'ACTIVE'}
                      </div>
                    </div>
                  ))}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={queryParams.pageSize ?? 25}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '0.5rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                        <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange((queryParams.page ?? 1) - 2)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>
                        Page {queryParams.page ?? 1} of {Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(queryParams.page ?? 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25))) - 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                        <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { handleClearFilters(); setFilterOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                Notice Type Code
              </label>
              <input
                type="text"
                className="form-input"
                value={noticeTypeCodeFilter}
                onChange={(e) => setNoticeTypeCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                Notice Type Name
              </label>
              <input
                type="text"
                className="form-input"
                value={noticeTypeNameFilter}
                onChange={(e) => setNoticeTypeNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'Active only (default)' },
                  { value: 'INACTIVE', label: 'Inactive only' },
                  { value: 'DELETED', label: 'Deleted only' },
                  { value: 'ALL', label: 'All (incl. deleted)' },
                ]}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={
          ntToDelete
            ? `Are you sure you want to delete notice type "${ntToDelete.noticeTypeCode}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setNTToDelete(null);
          }
        }}
      />

      <NoticeTypeFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedNT}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default NoticeTypeMasterScreen;
